// Client-side wrapper around /api/wow/lookup. In-memory cache so the same
// (lang, type, id) is fetched at most once per page load.

import { getLang } from '@shared/i18n';

export interface LookupResult {
  id: number;
  type: 'spell' | 'item';
  lang: 'en' | 'zh';
  name: string | null;
  icon: string | null;
  description: string | null;
  tooltipHtml: string | null;
}

const memo = new Map<string, Promise<LookupResult>>();

export function lookup(type: 'spell' | 'item', id: number): Promise<LookupResult> {
  const lang = getLang();
  const key = `${lang}:${type}:${id}`;
  let p = memo.get(key);
  if (!p) {
    p = (async (): Promise<LookupResult> => {
      // Version param invalidates HTTP cache when the response shape changes
      // (e.g. when we started returning tooltipHtml for items). Bump it
      // alongside any worker-side response-format change.
      const url = `/api/wow/lookup?type=${type}&id=${id}&lang=${lang}&v=2`;
      try {
        const r = await fetch(url, { cache: 'no-cache' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as LookupResult;
        const isFullSuccess = !!data.name && (data.type !== 'item' || !!data.tooltipHtml);
        if (!isFullSuccess) {
          // Retry once after a short delay — usually the worker will hit the
          // wowhead cache the second time and return the full payload.
          memo.delete(key);
          await new Promise((res) => setTimeout(res, 200));
          try {
            const r2 = await fetch(url, { cache: 'no-cache' });
            if (r2.ok) {
              const retry = (await r2.json()) as LookupResult;
              if (retry.name && (retry.type !== 'item' || retry.tooltipHtml)) return retry;
            }
          } catch { /* fall through with the partial first response */ }
        }
        return data;
      } catch (err) {
        memo.delete(key);
        return { id, type, lang, name: null, icon: null, description: null, tooltipHtml: null };
      }
    })();
    memo.set(key, p);
  }
  return p;
}

export function clearLookupCache(): void {
  memo.clear();
}

// Icons are proxied through our /api/img endpoint so users in regions that
// ZAM Network has geo-blocked (China and a few others as of 2026) still get
// images. The upstream path stays the same — we just swap the host.
const ICON_BASE = '/api/img/images/wow/icons/medium';
export function iconNameToUrl(iconName: string): string {
  return `${ICON_BASE}/${iconName.toLowerCase()}.jpg`;
}
